import { t } from 'i18next'
import SecureCookieManager from '../../utils/secureCookie';
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaPlus } from 'react-icons/fa';
import { Breadcrumb, BreadcrumbItem, Input, Modal, ModalHeader, ModalBody, ModalFooter, Button as ReactstrapButton, Badge, Tooltip } from 'reactstrap'
import Button from 'src/components/Buttons/Button';
import './style.css';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getUserInfo, getUserRolePermissions } from '../../services/userServicer';
import { getAnnouncement } from '../../services/announcementServices';
import { FiFile } from "react-icons/fi";
import { useSearchQuery } from '../../hook/useSearchQuery';
import LoadingScreen from 'src/Loading/LoadingScreen';
import authService from 'src/services/authService';

export default function Announcement() {
const {t}=useTranslation();
const [currentPage, setCurrentPage] = useState(0);
const [perPage, setPerPage] = useState<number>(10);
const [searchTerm, setSearchTerm] = useState("");
const [debouncedSearch, setDebouncedSearch] = useState("");
const [data, setData] = useState<any[]>([]);
const [totalPages, setTotalPages] = useState<number>(0);
const [showPublishModal, setShowPublishModal] = useState(false);
const [announcementMessage, setAnnouncementMessage] = useState("");
const [selectedTenants, setSelectedTenants] = useState<string[]>([]);


const [uploadedFile, setUploadedFile] = useState<any>(null);
const [permissions, setPermissions] = useState<any>(null);
const [notAllowed, setNotAllowed] = useState(false);


const [sortConfig, setSortConfig] = useState<{
  key: keyof any | "index";
  direction: "ascending" | "descending" | null;
}>({ key: "index", direction: "ascending" });


const [loading, setLoading] = useState<boolean>(false);
const [permissionsLoading, setPermissionsLoading] = useState<boolean>(true);
const [tableLoading, setTableLoading] = useState<boolean>(false);

// Use the search query hook
const { searchQuery, searchResultType, clearSearchQuery } = useSearchQuery();


const fetchAnnouncementData = async (page: number, query: string) => {
  setTableLoading(true);
  try {
    const response: any = await getAnnouncement(query, perPage, page);   
    if (response && response.success) {
      const indexedData = response.data.map((user: any, idx: number) => ({
        ...user,
        index: idx + 1 + page * perPage,
      }));
      setData(indexedData);
      setTotalPages(Math.ceil(response.totalCount / perPage));
    } else if (response && response.data.length === 0) {
      setData([]);
      setTotalPages(0);
    } else {
      toast.error(t("announcement.failed_to_fetch_announcement_data"));
    }
  } catch (error: any) {
    toast.error(t("announcement.error_fetching_announcement_data")+":", error);
  } finally {
    setTableLoading(false);
  }
};



// Separate useEffect for permissions - runs first
useEffect(() => {
  const fetchPermissions = async () => {
    setPermissionsLoading(true);
    try {
      const user = await getUserInfo();
      const response = await getUserRolePermissions(user.id);
      if (response.success) {
        const pagePermission = response.data.permissions.find((p: any) => p.page === 'announcement');
        if (pagePermission) {
          setPermissions(pagePermission.permissions);
          setNotAllowed(!pagePermission.permissions.show);
        } else {
          setNotAllowed(true);
        }
      } else {
        setNotAllowed(true);
      }
    } catch (error) {
      setNotAllowed(true);
    } finally {
      setPermissionsLoading(false);
    }
  };

  fetchPermissions();
}, []); // Only run once on component mount

// Debounce search term (1s)
useEffect(() => {
  const handle = setTimeout(() => {
    setDebouncedSearch(searchTerm.trim());
  }, 1000);
  return () => clearTimeout(handle);
}, [searchTerm]);

// Reset to first page on search term change (immediately)
useEffect(() => {
  setCurrentPage(0);
}, [searchTerm]);

// Separate useEffect for data fetching - only runs after permissions are loaded and debounce elapsed
useEffect(() => {
  if (!permissionsLoading && !notAllowed) {
    fetchAnnouncementData(currentPage, debouncedSearch);
  }
}, [perPage, currentPage, permissionsLoading, notAllowed, debouncedSearch]);


const sortedData = useMemo(() => {
  let sortableData = [...data];
  if (sortConfig.direction !== null) {
    sortableData.sort((a: any, b: any) => {
      if (sortConfig.key === "index") {
        return sortConfig.direction === "ascending" ? a.index - b.index : b.index - a.index;
      }
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === "ascending" ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === "ascending" ? 1 : -1;
      }
      return 0;
    });
  }
  return sortableData;
}, [data, sortConfig, currentPage, perPage]);

const filteredData = useMemo(() => {
  const filtered = sortedData.filter((item) => {
    return (
      item.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.to?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.message?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.status?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });
 // console.log('Filtered Data:', filtered);
  return filtered;
}, [sortedData, searchTerm, currentPage, perPage]);

useEffect(() => {
  setCurrentPage(0);
}, [searchTerm]);

// Handle search query from global search
useEffect(() => {
  if (searchQuery && searchResultType === 'announcements') {
    setSearchTerm(searchQuery);
    clearSearchQuery();
  }
}, [searchQuery, searchResultType, clearSearchQuery]);

  const handleAddAnnouncement = () => {
    setShowPublishModal(true);
  }

const toggleSort = (key: keyof any | "index") => {
  let direction: "ascending" | "descending" | null = "ascending";
  if (sortConfig.key === key && sortConfig.direction === "ascending") {
    direction = "descending";
  } else if (sortConfig.key === key && sortConfig.direction === "descending") {
    direction = null;
  }
  setSortConfig({ key, direction });
};

// Show loading while checking permissions
if (permissionsLoading) {
  return (
    <div className="loading-screen" 
    style={{ 
      backgroundColor: 'rgba(255, 255, 255, 0.8)'}}>
                   <div><LoadingScreen/></div>
                 </div>
  );
}

// Show permission denied message if not allowed
if (notAllowed) {
  return (
    <div style={{marginTop:"75px"}} className="alert alert-danger text-center mt-10">
      {t("permissions.not_allowed")}
    </div>
  );
}

// Note: we no longer block the whole page with a global loader for data; we use a table loader below

const openFileInNewTab = async (fileUrl: string) => {
  try {
    const response = await authService.apiCall(fileUrl, {
      method: 'GET'
    });

    if (!response.ok) {
      toast("Failed to fetch file.", { type: "error" });
      return;
    }

    const blob = await response.blob();
    const fileObjectURL = URL.createObjectURL(blob);

    const newTab = window.open();
    if (newTab) {
      if (blob.type.startsWith("image/")) {
        newTab.document.write(`<img src="${fileObjectURL}" alt="Secure File" />`);
      } else {
        newTab.document.write(`
          <div style="text-align: center; margin: 20px;">
            <h2 style="color: #333;">${fileUrl.split('/').pop()}</h2>
            <a href="${fileObjectURL}" download target="_blank"
               style="padding: 10px 20px; background-color: orange; color: white; border-radius: 5px; text-decoration: none;">
              Download File
            </a>
          </div>
        `);
      }
    }
  } catch (error: any) {
    toast(error?.message || "Error opening file", { type: "error" });
  }
};
const canView = permissions?.show;
const canCreate = permissions?.create;

  return (
    <div className="root">
      
      
      


      <div className="d-flex justify-content-between align-items-center mb-lg ">
        <h5 className="">{t("announcement.page_title_announcement")}</h5>
       
      </div>


      <div className="d-flex justify-content-between my-2  rounded  p-4"style={{ backgroundColor: 'var(--widget-bg)' }}>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t("announcement.search_placeholder") || ''}
            className="search-input"
          />
         
        </div>

       <div className="container_info" >
       
    
        <table className="styled-table">
          <thead>

            <tr>
              <th onClick={() => toggleSort("index")} className="sortable-header">
                #
                {sortConfig.key === "index"
                  ? sortConfig.direction === "ascending"
                    ? "🔼"
                    : "🔽"
                  : ""}
              </th>
              <th onClick={() => toggleSort("subject")} className="sortable-header">
                {t("announcement.subject")} {" "}
                {sortConfig.key === "subject"
                  ? sortConfig.direction === "ascending"
                    ? "🔼"
                    : "🔽"
                  : ""}
              </th>
              <th onClick={() => toggleSort("to")} className="sortable-header">
                {t("announcement.to")} {" "}
                {sortConfig.key === "to"
                  ? sortConfig.direction === "ascending"
                    ? "🔼"
                    : "🔽"
                  : ""}
              </th>
              <th onClick={() => toggleSort("announcement")} className="sortable-header">
                {t("announcement.announcement")} {" "}
                {sortConfig.key === "announcement"
                  ? sortConfig.direction === "ascending"
                    ? "🔼"
                    : "🔽"
                  : ""}
              </th>
              <th>{t('announcement.Attachment')}</th>
              <th>{t("announcement.status")}</th>
            </tr>
          </thead>

          {tableLoading && (
            <tbody>
              <tr>
                <td colSpan={6}>
                  <div className="d-flex justify-content-center py-4">
                    <div className="spinner-border text-warning" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          )}

          {!tableLoading && data.length > 0 && (
            <tbody>
              {filteredData.map((item: any, index) => (
                <React.Fragment key={index}>
                  <tr>
                    <td>{index + 1}</td>
                    <td>{item.subject}</td>
                    <td>{item.to}</td>
                    <td>{item.message}</td>
                    <td>
                    {item.files?.map((file: any, index: number) => (
                      <FiFile
                        key={index}
                        id={`tooltip-file-${index}`}
                        color="orange"
                        size={20}
                        className="mr-2 cursor-pointer"
                        title={file.name}
                        onClick={() => openFileInNewTab(`${process.env.REACT_APP_API_URL}${file.src}?current=announcement`)}
                      />
                    ))}
                    </td>
                    <td>
                    <span className="badge bg-success-app">
                  {t('announcement.sent')}
                    </span>
                    </td>
                    
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          )}
          {!tableLoading && data.length === 0 && (
            <tbody>
              <tr>
                <td colSpan={6} className="text-center alert alert-warning">
                  {t("announcement.no_announcement_available")}
                </td>
              </tr>
            </tbody>
          )}
        </table>

        <div className="pagination-controls mt-3">
  <button
    className="btn btn-warning btn-sm text-white"
    onClick={() => setCurrentPage(0)}
    disabled={currentPage === 0}
  >
    {"<<"}
  </button>{" "}
  <button
    className="btn btn-warning btn-sm text-white"
    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
    disabled={currentPage === 0}
  >
    {"<"}
  </button>{" "}
  <button
    className="btn btn-warning btn-sm text-white"
    onClick={() =>
      setCurrentPage((prev) => Math.min(prev + 1, totalPages))
    }
    disabled={currentPage+1 >= totalPages}
  >
    {">"}
  </button>{" "}
  <button
    className="btn btn-warning btn-sm text-white"
    onClick={() => setCurrentPage(totalPages)}
    disabled={currentPage+1 >= totalPages}
  >
    {">>"}
  </button>{" "}
  <span>
    {t("announcement.page")} {currentPage+1} {t("announcement.to")} {totalPages}
  </span>
      </div>
      
      </div>

      
  
    </div>
  )
}
